/**
 * Lending Account Management
 *
 * This module provides comprehensive account management functionality for the lending protocol.
 * It includes coin merging, health factor calculations, user state management, and various
 * lending operations like supply, borrow, withdraw, and repay.
 *
 * @module LendingAccount
 */

import type {
  UserLendingInfo,
  SuiClientOption,
  EnvOption,
  Pool,
  Transaction as NAVITransaction,
  AssetIdentifier,
  TransactionResult,
  CacheOption,
  AccountCap,
  MarketOption,
  LendingPosition,
  MarketsOption,
  EModePool,
  EModeCap,
  ServiceOption
} from './types'
import { Transaction } from '@mysten/sui/transactions'
import { UserStateInfo, ReserveDataInfo } from './bcs'
import { getConfig, DEFAULT_CACHE_TIME } from './config'
import {
  parseDevInspectResult,
  devInspectTransaction,
  withSingleton,
  processContractHealthFactor,
  parseTxValue,
  parseTxPoolValue,
  withCache,
  normalizeCoinType,
  rayMathMulIndex,
  requestHeaders,
  poolToEModePool,
  getPoolsMap,
  uuid,
  suiClient
} from './utils'
import { bcs } from '@mysten/sui/bcs'
import type { CoinStruct, PaginatedCoins } from '@mysten/sui/jsonRpc'
import { getPool, PoolOperator, getPools } from './pool'
import packageJson from '../package.json'
import { getUserEModeCaps } from './emode'
import BigNumber from 'bignumber.js'
import { getMarketConfig, MARKETS } from './market'
import { buildNaviOpenApiUrl, mergeServiceHeaders, resolveNaviOpenApiEndpoint } from './services'

function getCoreCoinType(coin: any, fallbackCoinType?: string) {
  if (coin.coinType) return coin.coinType
  if (fallbackCoinType) return fallbackCoinType

  const match = typeof coin.type === 'string' ? coin.type.match(/::coin::Coin<(.+)>$/) : null
  return match?.[1]
}

function normalizeCoreCoin(coin: any, fallbackCoinType?: string): CoinStruct | null {
  const coinType = getCoreCoinType(coin, fallbackCoinType)
  const coinObjectId = coin.coinObjectId ?? coin.objectId
  if (!coinType || !coinObjectId || coin.balance === undefined || coin.balance === null) {
    return null
  }

  return {
    ...coin,
    coinType: normalizeCoinType(coinType),
    coinObjectId
  } as CoinStruct
}

/**
 * Merges multiple coins into a single coin for transaction building
 *
 * This function takes multiple coin objects and merges them into a single coin
 * that can be used in a transaction. It supports optional splitting to create
 * a specific balance amount.
 *
 * @param tx - Transaction object to add merge operations to
 * @param coins - Array of coin objects to merge
 * @param options - Optional parameters for balance splitting and gas coin usage
 *   - `balance` - If provided, splits this amount from the resulting coin object
 *   - `useGasCoin` - If true, uses the gas coin for the operation
 *   - `addressBalance` - Funds held directly at the address level (v2 address
 *     balances) for this coin type. `coins` only carries `Coin<T>` objects, so
 *     when they fall short of `balance` this covers the remainder. Defaults to
 *     `0`, which preserves the legacy coin-object-only behavior exactly.
 *   - `coinType` - Coin type used to withdraw the shortfall from the address
 *     balance via `0x2::coin::redeem_funds`. Required for the address-balance
 *     path; ignored when the owned coin objects already cover `balance`.
 * @returns Transaction result representing the merged coin
 */
export function mergeCoinsPTB(
  tx: Transaction,
  coins: ({
    balance: string | number | bigint
    coinObjectId: string
    coinType: string
  } & CoinStruct)[],
  options?: {
    balance?: number
    useGasCoin?: boolean
    addressBalance?: string | number | bigint
    coinType?: string
  }
) {
  const needSplit = typeof options?.balance === 'number'
  const splitBalance = needSplit ? options.balance! : 0
  const addressBalance = BigInt(options?.addressBalance ?? 0)
  let mergedBalance = 0
  const mergeList: string[] = []
  let coinType = ''
  // Full sum of the passed coin objects (used for the combined sufficiency
  // check and to size the address-balance shortfall). Kept separate from
  // `mergedBalance`, which can stop early once `splitBalance` is reached.
  let objectsBalance = 0n

  // Sort coins by balance (highest first) and collect valid coins
  coins
    .sort((a, b) => Number(b.balance) - Number(a.balance))
    .forEach((coin) => {
      objectsBalance += BigInt(coin.balance)
      if (needSplit && mergedBalance >= splitBalance) {
        return
      }
      if (Number(coin.balance) === 0) {
        return
      }
      if (!coinType) {
        coinType = coin.coinType
      }
      if (coinType !== coin.coinType) {
        throw new Error('All coins must be of the same type')
      }
      mergedBalance += Number(coin.balance)
      mergeList.push(coin.coinObjectId)
    })

  // Coin type driving the SUI/gas decision and the address-balance withdrawal.
  // When there are no coin objects the local `coinType` (derived from
  // `coins[i].coinType`) is empty, so fall back to the caller-supplied
  // `options.coinType` — required for the address-only case, and it also avoids
  // passing an empty string into `normalizeCoinType` (which would throw).
  const redeemCoinType = options?.coinType
  const effectiveType = coinType || redeemCoinType || ''
  // Tolerant SUI check: an unparseable/placeholder coin type is simply "not SUI"
  // and must not throw before the balance guards (which run below), matching the
  // original ordering.
  const isSuiType = (type: string) => {
    if (!type) {
      return false
    }
    try {
      return normalizeCoinType(type) === normalizeCoinType('0x2::sui::SUI')
    } catch {
      return false
    }
  }
  const isSuiGas = isSuiType(effectiveType) && !!options?.useGasCoin

  // Address balance is only actually spendable when we can redeem it: on the
  // non-gas path AND with a coinType to withdraw against. The SUI gas-coin path
  // cannot draw from it, and without a coinType there is no redeem_funds call —
  // so in those cases it must not count toward the sufficiency check (otherwise
  // the combined total would pass and we'd split against coin objects that are
  // still short).
  const need = BigInt(splitBalance)
  const canUseAddressBalance = !isSuiGas && !!redeemCoinType
  const usableAddressBalance = canUseAddressBalance ? addressBalance : 0n
  const combined = objectsBalance + usableAddressBalance
  // Shortfall the address balance must cover. Only meaningful when splitting.
  // `combined >= need` (checked below) guarantees `shortfall <= usableAddressBalance`.
  const shortfall = !needSplit || objectsBalance >= need ? 0n : need - objectsBalance
  const canRedeem = canUseAddressBalance && shortfall > 0n && usableAddressBalance >= shortfall

  // Insufficiency first: when splitting and the (usable) combined total is short,
  // report the balance shortfall — including the address-only case (no coin
  // objects but some address balance) — rather than the misleading empty-wallet
  // "No coins to merge".
  if (needSplit && combined < need) {
    throw new Error(`Balance is less than the specified balance: ${combined} < ${need}`)
  }
  // Genuinely nothing to work with: no coin objects and no address-balance
  // shortfall to redeem. Preserves the legacy "No coins to merge" behavior.
  if (mergeList.length === 0 && !canRedeem) {
    throw new Error('No coins to merge')
  }

  // Handle SUI gas coin specially. SUI stays on the gas-split path and is never
  // routed through redeem_funds.
  if (isSuiGas) {
    return needSplit ? tx.splitCoins(tx.gas, [tx.pure.u64(splitBalance)]) : tx.gas
  }

  // Legacy coin-object-only path: the owned objects already cover the request
  // (or no address balance is in play). Behavior is byte-for-byte unchanged.
  if (!canRedeem) {
    mergeList.length === 1
      ? tx.object(mergeList[0])
      : tx.mergeCoins(mergeList[0], mergeList.slice(1))

    return needSplit ? tx.splitCoins(mergeList[0], [tx.pure.u64(splitBalance)]) : mergeList[0]
  }

  // Address-balance path: withdraw the shortfall from the address balance and
  // merge it with the owned coin objects (largest first). When there are no
  // coin objects at all, the redeemed coin becomes the base.
  const redeemedCoin = tx.moveCall({
    target: '0x2::coin::redeem_funds',
    typeArguments: [redeemCoinType!],
    arguments: [tx.withdrawal({ amount: shortfall, type: redeemCoinType! })]
  })

  let baseCoin: any
  const restCoins: any[] = []
  if (mergeList.length > 0) {
    baseCoin = tx.object(mergeList[0])
    for (const id of mergeList.slice(1)) {
      restCoins.push(tx.object(id))
    }
    restCoins.push(redeemedCoin)
  } else {
    baseCoin = redeemedCoin
  }

  if (restCoins.length > 0) {
    tx.mergeCoins(baseCoin, restCoins)
  }

  // Pure address-balance case: the base is the redeemed coin (no owned coin
  // objects), and shortfall === splitBalance, so it already holds exactly the
  // requested amount. Return it directly — splitting the full amount would leave
  // a zero-balance redeemed Coin result unused, and Coin has no `drop` ability,
  // so the PTB would fail. (Mixed object + address paths split from an owned
  // object base, whose leftover stays in place and is fine.)
  if (mergeList.length === 0) {
    return baseCoin
  }

  return needSplit ? tx.splitCoins(baseCoin, [tx.pure.u64(splitBalance)]) : baseCoin
}

/**
 * Calculates dynamic health factor for a user after potential operations
 *
 * This function creates a transaction call to calculate the health factor
 * that would result after performing supply/borrow operations.
 *
 * @param tx - Transaction object to append calculation to
 * @param address - User address or transaction result
 * @param identifier - Asset identifier
 * @param estimatedSupply - Estimated supply amount
 * @param estimatedBorrow - Estimated borrow amount
 * @param isIncrease - Whether this is an increase operation
 * @param options - Environment options
 * @returns Transaction result for health factor calculation
 */
export async function getSimulatedHealthFactorPTB(
  tx: Transaction,
  address: string | AccountCap | TransactionResult,
  identifier: AssetIdentifier,
  estimatedSupply: number | TransactionResult,
  estimatedBorrow: number | TransactionResult,
  isIncrease: boolean | TransactionResult,
  options?: Partial<EnvOption & MarketOption>
): Promise<TransactionResult> {
  const config = await getConfig({
    ...options,
    cacheTime: DEFAULT_CACHE_TIME
  })
  const pool = await getPool(identifier, options)
  return tx.moveCall({
    target: `${config.uiGetter}::calculator_unchecked::dynamic_health_factor`,
    arguments: [
      tx.object('0x06'),
      tx.object(config.storage),
      tx.object(config.oracle.priceOracle),
      parseTxPoolValue(tx, pool),
      parseTxValue(address, tx.pure.address),
      parseTxValue(pool.id, tx.pure.u8),
      parseTxValue(estimatedSupply, tx.pure.u64),
      parseTxValue(estimatedBorrow, tx.pure.u64),
      parseTxValue(isIncrease, tx.pure.bool)
    ],
    typeArguments: [pool.suiCoinType]
  })
}

/**
 * Gets the current health factor for a user
 *
 * @param tx - The transaction object to add health factor query operation to
 * @param address - User address or account cap or transaction result
 * @param options - Environment options
 * @returns Transaction result for health factor calculation
 */
export async function getHealthFactorPTB(
  tx: Transaction,
  address: string | AccountCap | TransactionResult,
  options?: Partial<EnvOption>
): Promise<TransactionResult> {
  return getSimulatedHealthFactorPTB(tx, address, 0, 0, 0, false, options)
}

async function getLendingStateBatch(
  address: string,
  tasks: {
    address: string
    market: string
    emodeId?: number
  }[],
  options?: Partial<
    SuiClientOption &
      EnvOption &
      CacheOption & {
        includeZeroBalanceEmodePositions?: boolean
      }
  >
): Promise<UserLendingInfo[]> {
  const tx = new Transaction()
  const client = options?.client ?? suiClient
  const pools = await getPools({
    ...options,
    markets: Object.values(MARKETS)
  })
  const poolsMap = getPoolsMap(pools)

  // Read the on-chain reserve indices for every market referenced by the
  // tasks in the SAME PTB as get_user_state. This guarantees that
  // supply_balance / borrow_balance and supply_index / borrow_index are
  // captured from the exact same chain snapshot. Without this, indices come
  // from the cached open-api /api/navi/pools response (typically lagging the
  // chain by seconds to minutes), so right after a deposit/withdraw the
  // displayed amount can drift by the interest accrued in between.
  const uniqueMarketKeys = Array.from(new Set(tasks.map((task) => task.market)))
  const uniqueMarkets = uniqueMarketKeys.map((key) => getMarketConfig(key))

  for (const market of uniqueMarkets) {
    const config = await getConfig({
      ...options,
      cacheTime: DEFAULT_CACHE_TIME,
      market: market.key
    })
    tx.moveCall({
      target: `${config.uiGetter}::getter::get_reserve_data`,
      arguments: [tx.object(config.storage)]
    })
  }

  for (let task of tasks) {
    const config = await getConfig({
      ...options,
      cacheTime: DEFAULT_CACHE_TIME,
      market: task.market
    })
    tx.moveCall({
      target: `${config.uiGetter}::getter_unchecked::get_user_state`,
      arguments: [tx.object(config.storage), tx.pure.address(task.address)]
    })
  }

  const resp = await devInspectTransaction(client, {
    transactionBlock: tx,
    sender: address
  })

  const results = resp.results || []

  const reserveIndicesByMarket: Record<
    string,
    Record<number, { supplyIndex: string; borrowIndex: string }>
  > = {}
  uniqueMarkets.forEach((market, idx) => {
    try {
      const reserves =
        results[idx]?.returnValues?.map((item: any) => {
          return bcs.vector(ReserveDataInfo as any).parse(Uint8Array.from(item[0]))
        })[0] || []
      const perAsset: Record<number, { supplyIndex: string; borrowIndex: string }> = {}
      for (const reserve of reserves as Array<{
        id: number
        supply_index: string
        borrow_index: string
      }>) {
        perAsset[reserve.id] = {
          supplyIndex: reserve.supply_index,
          borrowIndex: reserve.borrow_index
        }
      }
      reserveIndicesByMarket[market.key] = perAsset
    } catch (e) {
      // BCS decode failed (e.g. on-chain ReserveDataInfo layout changed and
      // our schema is out of date). Leave the entry empty so the per-asset
      // `?? pool.currentSupplyIndex` fallback below kicks in for this
      // market only, preserving the original 1.4.4 behaviour for the
      // affected market instead of turning a recoverable mismatch into a
      // hard failure that wipes out every position for the user.
      console.warn(
        `[@naviprotocol/lending] Failed to decode reserve data for market "${market.key}", falling back to open-api pool indices`,
        e
      )
    }
  })

  const stateList = results.slice(uniqueMarkets.length).map((result: any) => {
    return (
      result.returnValues?.map((item: any) => {
        return bcs.vector(UserStateInfo as any).parse(Uint8Array.from(item[0]))
      })[0] || []
    )
  }) as {
    supply_balance: string
    borrow_balance: string
    asset_id: number
  }[][]

  const result = [] as UserLendingInfo[]

  stateList.forEach((states, index) => {
    const task = tasks[index]
    const market = getMarketConfig(task.market)
    const marketReserves = reserveIndicesByMarket[market.key] || {}
    states.forEach((state) => {
      if (state.supply_balance === '0' && state.borrow_balance === '0') {
        if (task.emodeId === undefined) {
          return
        }
        if (!options?.includeZeroBalanceEmodePositions) {
          return
        }
      }
      const pool = poolsMap[`${market.key}-${state.asset_id}`]
      if (!pool) {
        return
      }
      // Prefer on-chain indices captured in the same PTB; fall back to the
      // open-api pool indices only when reserve data could not be decoded
      // for this asset (keeps behaviour backward compatible if the chain
      // ever stops exposing getter::get_reserve_data).
      const reserveIndices = marketReserves[state.asset_id]
      const supplyIndex = reserveIndices?.supplyIndex ?? pool!.currentSupplyIndex
      const borrowIndex = reserveIndices?.borrowIndex ?? pool!.currentBorrowIndex
      const supplyBalance = rayMathMulIndex(state.supply_balance, supplyIndex).toString()
      const borrowBalance = rayMathMulIndex(state.borrow_balance, borrowIndex).toString()
      result.push({
        supplyBalance,
        borrowBalance,
        assetId: state.asset_id,
        market: market.key,
        pool,
        emodeId: task.emodeId
      })
    })
  })

  return result
}

/**
 * Retrieves the current lending state for a user
 *
 * This function fetches all active lending positions for a user, including
 * supply and borrow balances for different assets.
 *
 * @param address - User wallet address or account cap
 * @param options - Options for client, environment, and caching
 * @returns Promise<UserLendingInfo[]> - Array of user lending positions
 */
export const getLendingState = withCache(
  async (
    address: string | AccountCap,
    options?: Partial<SuiClientOption & EnvOption & CacheOption & MarketsOption>
  ): Promise<UserLendingInfo[]> => {
    const markets = (options?.markets || Object.keys(MARKETS)).map((item) => {
      return getMarketConfig(item)
    })

    const tasks = markets.map((market) => {
      return {
        address,
        market: market.key
      }
    })

    return await getLendingStateBatch(address, tasks, options)
  }
)

/**
 * Calculates the current health factor for a user
 *
 * @param address - User wallet address or account cap
 * @param options - Options for client and environment
 * @returns Promise<number> - Health factor value
 */
export async function getHealthFactor(
  address: string | AccountCap,
  options?: Partial<SuiClientOption & EnvOption>
): Promise<number> {
  const client = options?.client ?? suiClient
  const tx = new Transaction()
  await getHealthFactorPTB(tx, address, options)
  const result = await devInspectTransaction(client, {
    transactionBlock: tx,
    sender: address
  })
  const res = parseDevInspectResult<number[]>(result, [bcs.u256()])
  return processContractHealthFactor(Number(res[0]) || 0)
}

/**
 * Calculates the health factor after performing lending operations
 *
 * This function simulates the health factor that would result after
 * performing a series of supply, withdraw, borrow, or repay operations.
 *
 * @param address - User wallet address or account cap
 * @param identifier - Asset identifier
 * @param operations - Array of operations to simulate
 * @param options - Options for client and environment
 * @returns Promise<number> - Projected health factor
 */
export async function getSimulatedHealthFactor(
  address: string | AccountCap,
  identifier: AssetIdentifier,
  operations: {
    type: PoolOperator
    amount: number
  }[],
  options?: Partial<SuiClientOption & EnvOption>
): Promise<number> {
  const client = options?.client ?? suiClient
  const tx = new Transaction()
  let estimatedSupply = 0
  let estimatedBorrow = 0
  const pool = await getPool(identifier, options)

  // Calculate estimated changes from operations
  operations.forEach((operation) => {
    if (operation.type === PoolOperator.Supply) {
      estimatedSupply += operation.amount
    } else if (operation.type === PoolOperator.Withdraw) {
      estimatedSupply -= operation.amount
    } else if (operation.type === PoolOperator.Borrow) {
      estimatedBorrow += operation.amount
    } else if (operation.type === PoolOperator.Repay) {
      estimatedBorrow -= operation.amount
    }
  })

  // Validate operation consistency
  if (estimatedSupply * estimatedBorrow < 0) {
    throw new Error('Invalid operations')
  }

  // Determine if this is an increase operation
  const isIncrease = estimatedSupply > 0 || estimatedBorrow > 0

  // Calculate the dynamic health factor
  await getSimulatedHealthFactorPTB(
    tx,
    address,
    pool,
    Math.abs(estimatedSupply),
    Math.abs(estimatedBorrow),
    isIncrease,
    options
  )

  // Execute dry run to get the result
  const result = await devInspectTransaction(client, {
    transactionBlock: tx,
    sender: address
  })
  const res = parseDevInspectResult<number[]>(result, [bcs.u256()])
  return processContractHealthFactor(Number(res[0]) || 0)
}

/**
 * Retrieves transaction history for a user from the Navi protocol API
 *
 * This function fetches the transaction history for a specific user address
 * from the Navi protocol's open API. It supports pagination through cursor-based navigation.
 *
 * @param address - User wallet address or account cap
 * @param options - Optional parameters including cursor for pagination
 * @returns Promise with transaction data and optional cursor for next page
 */
export const getTransactions = withSingleton(
  async (
    address: string | AccountCap,
    options?: {
      cursor?: string
    } & ServiceOption
  ): Promise<{
    data: NAVITransaction[]
    cursor?: string
  }> => {
    // Build query parameters for the API request
    const params = new URLSearchParams()
    if (options?.cursor) {
      params.set('cursor', options.cursor)
    }
    params.set('userAddress', address)

    // Fetch transaction history from Navi protocol API
    const endpoint = resolveNaviOpenApiEndpoint(options)
    const url = buildNaviOpenApiUrl(
      `/navi/user/transactions?${params.toString()}&sdk=${packageJson.version}`,
      options
    )
    const res = await fetch(url, {
      headers: mergeServiceHeaders(requestHeaders, endpoint)
    }).then((res) => res.json())
    return res.data
  }
)

/**
 * Retrieves all coins owned by a user address
 *
 * This function fetches all coin objects owned by a specific address from the Sui blockchain.
 * It supports filtering by coin type and handles pagination automatically to retrieve all coins.
 *
 * @param address - User wallet address
 * @param options - Optional parameters including coin type filter and client options
 *   - `coinType` - If specified, retrieves only coins of this type
 *   - `client` - If provided, uses this `SuiClient` instance instead of the default client
 * @returns Promise<CoinStruct[]> - Array of coin objects owned by the address
 */
export async function getCoins(
  address: string,
  options?: Partial<
    {
      coinType?: string
    } & SuiClientOption
  >
): Promise<CoinStruct[]> {
  let cursor: string | undefined | null = null
  const allCoinDatas: CoinStruct[] = []
  const client = options?.client ?? suiClient
  const core = client.core as
    | {
        listBalances?(options: any): Promise<any>
        listCoins?(options: any): Promise<any>
      }
    | undefined

  // Fetch all coins using pagination
  do {
    let res: PaginatedCoins

    // Use specific coin type filter if provided, otherwise get all coins
    if (options?.coinType) {
      if (typeof core?.listCoins === 'function') {
        const response = await core.listCoins({
          owner: address,
          coinType: options?.coinType,
          cursor,
          limit: 100
        })
        res = {
          data: (response.objects ?? response.data ?? [])
            .map((coin: any) => normalizeCoreCoin(coin, options?.coinType))
            .filter(Boolean) as CoinStruct[],
          nextCursor: response.cursor ?? response.nextCursor ?? null,
          hasNextPage: response.hasNextPage ?? false
        } as PaginatedCoins
      } else {
        if (typeof client.getCoins !== 'function') {
          throw new Error('getCoins requires core.listCoins or an explicit legacy getCoins client')
        }
        res = await client.getCoins({
          owner: address,
          coinType: options?.coinType,
          cursor,
          limit: 100
        })
      }
    } else if (typeof core?.listBalances === 'function' && typeof core?.listCoins === 'function') {
      const coinTypes = new Set<string>()
      let balanceCursor: string | null | undefined = null
      do {
        const response = await core.listBalances({
          owner: address,
          cursor: balanceCursor,
          limit: 100
        })
        for (const balance of response.balances ?? []) {
          if (balance?.coinType) {
            coinTypes.add(balance.coinType)
          }
        }
        balanceCursor = response.cursor ?? response.nextCursor ?? null
      } while (balanceCursor)

      for (const coinType of coinTypes) {
        let coinCursor: string | null | undefined = null
        do {
          const response = await core.listCoins({
            owner: address,
            coinType,
            cursor: coinCursor,
            limit: 100
          })
          allCoinDatas.push(
            ...((response.objects ?? response.data ?? [])
              .map((coin: any) => normalizeCoreCoin(coin, coinType))
              .filter(Boolean) as CoinStruct[])
          )
          coinCursor = response.cursor ?? response.nextCursor ?? null
        } while (coinCursor)
      }
      break
    } else {
      if (typeof client.getAllCoins !== 'function') {
        throw new Error(
          'getCoins requires core.listBalances/core.listCoins or an explicit legacy getAllCoins client'
        )
      }
      res = await client.getAllCoins({
        owner: address,
        cursor,
        limit: 100
      })
    }

    // Break if no more data
    if (!res.data || !res.data.length) {
      break
    }

    // Collect coin data and continue with next page
    allCoinDatas.push(...res.data)
    cursor = res.nextCursor
  } while (cursor)

  return allCoinDatas
}

export const getLendingPositions = withCache(
  async (
    address: string,
    options?: Partial<
      SuiClientOption &
        EnvOption &
        CacheOption &
        MarketsOption & {
          includeZeroBalanceEmodePositions?: boolean
        }
    >
  ): Promise<LendingPosition[]> => {
    const positions: LendingPosition[] = []
    const markets = (options?.markets || Object.keys(MARKETS)).map((item) => {
      return getMarketConfig(item)
    })

    // Do not silently swallow E-Mode cap failures: the previous catch fell back
    // to an empty array, silently dropping the user's E-Mode positions (and
    // masking real endpoint/RPC errors). Throw so callers are aware
    // instead of returning incomplete positions.
    const emodeCaps: EModeCap[] = await getUserEModeCaps(address, options)

    const tasks = markets
      .map((market) => {
        return {
          address,
          market: market.key
        }
      })
      .concat(
        emodeCaps
          .filter((cap) => {
            return !!markets.find((market) => market.id === cap.marketId)
          })
          .map((emodeCap) => {
            return {
              address: emodeCap.accountCap,
              market: getMarketConfig(emodeCap.marketId).key,
              emodeId: emodeCap.emodeId
            }
          })
      )

    const lendingStates = await getLendingStateBatch(address, tasks, options)

    lendingStates.forEach((lendingState) => {
      const emodeCap =
        typeof lendingState.emodeId === 'number'
          ? emodeCaps.find((cap) => {
              const market = getMarketConfig(lendingState.market)
              return cap.emodeId === lendingState.emodeId && cap.marketId === market.id
            })
          : undefined
      if (emodeCap) {
        const inEmode = lendingState.pool.emodes.find((emode) => emode.emodeId === emodeCap.emodeId)
        if (!inEmode) {
          return
        }
        if (BigNumber(lendingState.supplyBalance).gte(0)) {
          const supplyAmount = BigNumber(lendingState.supplyBalance)
            .shiftedBy(-9)
            .decimalPlaces(lendingState.pool.token.decimals, BigNumber.ROUND_DOWN)

          const emodePool = poolToEModePool(lendingState.pool, emodeCap)

          if (supplyAmount.gt(0) || emodePool.emode.isCollateral) {
            try {
              positions.push({
                id: `${lendingState.pool.uniqueId}_${emodeCap.emodeId}_navi-lending-emode-supply-${uuid()}`,
                wallet: address,
                protocol: 'navi',
                market: lendingState.market,
                type: 'navi-lending-emode-supply',
                'navi-lending-emode-supply': {
                  amount: supplyAmount.toString(),
                  pool: poolToEModePool(lendingState.pool, emodeCap),
                  token: lendingState.pool.token,
                  valueUSD: supplyAmount.multipliedBy(lendingState.pool.oracle.price).toString(),
                  emodeCap: emodeCap
                }
              })
            } catch (e) {
              console.error(e)
            }
          }
        }
        if (BigNumber(lendingState.borrowBalance).gte(0)) {
          const borrowAmount = BigNumber(lendingState.borrowBalance)
            .shiftedBy(-9)
            .decimalPlaces(lendingState.pool.token.decimals, BigNumber.ROUND_DOWN)

          const emodePool = poolToEModePool(lendingState.pool, emodeCap)

          if (borrowAmount.gt(0) || emodePool.emode.isDebt) {
            try {
              positions.push({
                id: `${lendingState.pool.uniqueId}_${emodeCap.emodeId}_navi-lending-emode-borrow-${uuid()}`,
                wallet: address,
                protocol: 'navi',
                market: lendingState.market,
                type: 'navi-lending-emode-borrow',
                'navi-lending-emode-borrow': {
                  amount: borrowAmount.toString(),
                  pool: poolToEModePool(lendingState.pool, emodeCap),
                  token: lendingState.pool.token,
                  valueUSD: borrowAmount.multipliedBy(lendingState.pool.oracle.price).toString(),
                  emodeCap: emodeCap
                }
              })
            } catch (e) {
              console.error(e)
            }
          }
        }
      } else {
        if (BigNumber(lendingState.supplyBalance).gt(0)) {
          const supplyAmount = BigNumber(lendingState.supplyBalance)
            .shiftedBy(-9)
            .decimalPlaces(lendingState.pool.token.decimals, BigNumber.ROUND_DOWN)
          positions.push({
            id: `${lendingState.pool.uniqueId}_navi-lending-supply-${uuid()}`,
            wallet: address,
            protocol: 'navi',
            type: 'navi-lending-supply',
            market: lendingState.market,
            'navi-lending-supply': {
              amount: supplyAmount.toString(),
              pool: lendingState.pool,
              token: lendingState.pool.token,
              valueUSD: supplyAmount.multipliedBy(lendingState.pool.oracle.price).toString()
            }
          })
        }
        if (BigNumber(lendingState.borrowBalance).gt(0)) {
          const borrowAmount = BigNumber(lendingState.borrowBalance)
            .shiftedBy(-9)
            .decimalPlaces(lendingState.pool.token.decimals, BigNumber.ROUND_DOWN)
          positions.push({
            id: `${lendingState.pool.uniqueId}_navi-lending-borrow-${uuid()}`,
            wallet: address,
            protocol: 'navi',
            market: lendingState.market,
            type: 'navi-lending-borrow',
            'navi-lending-borrow': {
              amount: borrowAmount.toString(),
              pool: lendingState.pool,
              token: lendingState.pool.token,
              valueUSD: borrowAmount.multipliedBy(lendingState.pool.oracle.price).toString()
            }
          })
        }
      }
    })
    return positions
  }
)

export type TokenPriceMap = Record<string, number>

export class UserPositions {
  private _positions: LendingPosition[] = []
  private _priceMap: TokenPriceMap = {}
  private _overview = {
    hf: Infinity,
    netVaule: '0',
    netWorthApr: '0',
    totalSupplyValue: '0',
    totalBorrowValue: '0',
    totalsupplyApy: '0',
    totalBorrowApy: '0',
    maxLiquidationValue: '0',
    maxLoanToVaule: '0',
    supply: {} as Record<string, string>,
    borrow: {} as Record<string, string>
  }

  get positions() {
    return this._positions
  }

  get overview() {
    return this._overview
  }

  get priceMap() {
    return this._priceMap
  }

  set positions(positions: LendingPosition[]) {
    this._positions = positions
    this._overview = this.getPositionsOverview(positions)
  }

  constructor(positions: LendingPosition[], priceMap?: TokenPriceMap) {
    this._priceMap = priceMap || {}
    this.positions = positions
  }

  public updatePriceMap(priceMap: TokenPriceMap) {
    this._priceMap = priceMap
    this._overview = this.getPositionsOverview(this._positions)
    return this
  }

  private getPrice(pool: Pool | EModePool): string {
    const coinType = normalizeCoinType(pool.suiCoinType)
    if (this._priceMap[coinType] !== undefined) {
      return this._priceMap[coinType].toString()
    }
    const rawType = pool.suiCoinType
    if (this._priceMap[rawType] !== undefined) {
      return this._priceMap[rawType].toString()
    }
    return pool.oracle.price
  }

  public filterPositionsByPool(pool: Pool | EModePool) {
    const isEmode = !!(pool as EModePool).isEMode
    const types = isEmode
      ? ['navi-lending-emode-supply', 'navi-lending-emode-borrow']
      : ['navi-lending-supply', 'navi-lending-borrow']
    return new UserPositions(
      this.positions.filter((position) => {
        const positionData = position[position.type]!
        return types.includes(position.type) && positionData.pool.uniqueId === pool.uniqueId
      }),
      this._priceMap
    )
  }

  public deposit(pool: Pool | EModePool, amount: number) {
    const isEmode = !!(pool as EModePool).isEMode
    const price = this.getPrice(pool)
    let position: LendingPosition
    if (isEmode) {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-emode-supply',
        'navi-lending-emode-supply': {
          amount: amount.toString(),
          valueUSD: BigNumber(amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as EModePool,
          emodeCap: {} as EModeCap
        }
      }
    } else {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-supply',
        'navi-lending-supply': {
          amount: amount.toString(),
          valueUSD: BigNumber(amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as Pool
        }
      }
    }
    return new UserPositions([...this.positions, position], this._priceMap)
  }

  public withdraw(pool: Pool | EModePool, amount: number) {
    const isEmode = !!(pool as EModePool).isEMode
    const price = this.getPrice(pool)
    let position: LendingPosition
    if (isEmode) {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-emode-supply',
        'navi-lending-emode-supply': {
          amount: (-amount).toString(),
          valueUSD: BigNumber(-amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as EModePool,
          emodeCap: {} as EModeCap
        }
      }
    } else {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-supply',
        'navi-lending-supply': {
          amount: (-amount).toString(),
          valueUSD: BigNumber(-amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as Pool
        }
      }
    }
    return new UserPositions([...this.positions, position], this._priceMap)
  }

  public borrow(pool: Pool | EModePool, amount: number) {
    const isEmode = !!(pool as EModePool).isEMode
    const price = this.getPrice(pool)
    let position: LendingPosition
    if (isEmode) {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-emode-borrow',
        'navi-lending-emode-borrow': {
          amount: amount.toString(),
          valueUSD: BigNumber(amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as EModePool,
          emodeCap: {} as EModeCap
        }
      }
    } else {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-borrow',
        'navi-lending-borrow': {
          amount: amount.toString(),
          valueUSD: BigNumber(amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as Pool
        }
      }
    }
    return new UserPositions([...this.positions, position], this._priceMap)
  }

  public repay(pool: Pool | EModePool, amount: number) {
    const isEmode = !!(pool as EModePool).isEMode
    const price = this.getPrice(pool)
    let position: LendingPosition
    if (isEmode) {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-emode-borrow',
        'navi-lending-emode-borrow': {
          amount: (-amount).toString(),
          valueUSD: BigNumber(-amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as EModePool,
          emodeCap: {} as EModeCap
        }
      }
    } else {
      position = {
        id: uuid(),
        wallet: '',
        protocol: 'navi',
        market: '',
        type: 'navi-lending-borrow',
        'navi-lending-borrow': {
          amount: (-amount).toString(),
          valueUSD: BigNumber(-amount).multipliedBy(price).toString(),
          token: pool.token,
          pool: pool as Pool
        }
      }
    }
    return new UserPositions([...this.positions, position], this._priceMap)
  }

  private resolveValueUSD(data: {
    amount: string
    valueUSD: string
    pool: Pool | EModePool
  }): string {
    const hasPriceMap = Object.keys(this._priceMap).length > 0
    if (!hasPriceMap) return data.valueUSD
    return BigNumber(data.amount).multipliedBy(this.getPrice(data.pool)).toString()
  }

  getPositionsOverview(positions: LendingPosition[]) {
    const supply = {} as Record<string, string>
    const borrow = {} as Record<string, string>
    let totalSupplyValue = new BigNumber(0)
    let totalBorrowValue = new BigNumber(0)
    let totalsupplyApy = new BigNumber(0)
    let totalBorrowApy = new BigNumber(0)
    let maxLiquidationValue = new BigNumber(0)
    let maxLoanToVaule = new BigNumber(0)
    positions.forEach((position) => {
      if (position.type === 'navi-lending-supply') {
        const data = position['navi-lending-supply']!
        const valueUSD = this.resolveValueUSD(data)
        totalSupplyValue = totalSupplyValue.plus(valueUSD)
        maxLiquidationValue = maxLiquidationValue.plus(
          new BigNumber(valueUSD).multipliedBy(data.pool.liquidationFactor.threshold)
        )
        maxLoanToVaule = maxLoanToVaule.plus(
          new BigNumber(valueUSD).multipliedBy(data.pool.ltvValue)
        )
      } else if (position.type === 'navi-lending-borrow') {
        const data = position['navi-lending-borrow']!
        totalBorrowValue = totalBorrowValue.plus(this.resolveValueUSD(data))
      } else if (position.type === 'navi-lending-emode-supply') {
        const data = position['navi-lending-emode-supply']!
        const valueUSD = this.resolveValueUSD(data)
        totalSupplyValue = totalSupplyValue.plus(valueUSD)
        const poolEmodeConfig = data.pool.emode
        maxLiquidationValue = maxLiquidationValue.plus(
          new BigNumber(valueUSD).multipliedBy(poolEmodeConfig.lt)
        )
        maxLoanToVaule = maxLoanToVaule.plus(
          new BigNumber(valueUSD).multipliedBy(poolEmodeConfig.ltv)
        )
      } else if (position.type === 'navi-lending-emode-borrow') {
        const data = position['navi-lending-emode-borrow']!
        totalBorrowValue = totalBorrowValue.plus(this.resolveValueUSD(data))
      }
    })

    totalBorrowValue = BigNumber.max(totalBorrowValue, 0)
    totalSupplyValue = BigNumber.max(totalSupplyValue, 0)
    maxLiquidationValue = BigNumber.max(maxLiquidationValue, 0)
    maxLoanToVaule = BigNumber.max(maxLoanToVaule, 0)

    positions.forEach((position) => {
      if (position.type === 'navi-lending-supply') {
        const data = position['navi-lending-supply']!
        const valueUSD = this.resolveValueUSD(data)
        const apy = data.pool.supplyIncentiveApyInfo.apy
        if (totalSupplyValue.gt(0)) {
          totalsupplyApy = totalsupplyApy.plus(
            new BigNumber(valueUSD)
              .dividedBy(totalSupplyValue)
              .multipliedBy(new BigNumber(apy).dividedBy(100))
          )
        }
        supply[data.pool.suiCoinType] = BigNumber(supply[data.pool.suiCoinType] || 0)
          .plus(data.amount)
          .toString()
      } else if (position.type === 'navi-lending-borrow') {
        const data = position['navi-lending-borrow']!
        const valueUSD = this.resolveValueUSD(data)
        const apy = data.pool.borrowIncentiveApyInfo.apy
        if (totalBorrowValue.gt(0)) {
          totalBorrowApy = totalBorrowApy.plus(
            new BigNumber(valueUSD)
              .dividedBy(totalBorrowValue)
              .multipliedBy(new BigNumber(apy).dividedBy(100))
          )
        }
        borrow[data.pool.suiCoinType] = BigNumber(borrow[data.pool.suiCoinType] || 0)
          .plus(data.amount)
          .toString()
      } else if (position.type === 'navi-lending-emode-supply') {
        const data = position['navi-lending-emode-supply']!
        const valueUSD = this.resolveValueUSD(data)
        const apy = data.pool.supplyIncentiveApyInfo.apy
        if (totalSupplyValue.gt(0)) {
          totalsupplyApy = totalsupplyApy.plus(
            new BigNumber(valueUSD)
              .dividedBy(totalSupplyValue)
              .multipliedBy(new BigNumber(apy).dividedBy(100))
          )
        }
        supply[data.pool.suiCoinType] = BigNumber(supply[data.pool.suiCoinType] || 0)
          .plus(data.amount)
          .toString()
      } else if (position.type === 'navi-lending-emode-borrow') {
        const data = position['navi-lending-emode-borrow']!
        const valueUSD = this.resolveValueUSD(data)
        const apy = data.pool.borrowIncentiveApyInfo.apy
        if (totalBorrowValue.gt(0)) {
          totalBorrowApy = totalBorrowApy.plus(
            new BigNumber(valueUSD)
              .dividedBy(totalBorrowValue)
              .multipliedBy(new BigNumber(apy).dividedBy(100))
          )
        }
        borrow[data.pool.suiCoinType] = BigNumber(borrow[data.pool.suiCoinType] || 0)
          .plus(data.amount)
          .toString()
      }
    })

    const netVaule = totalSupplyValue.minus(totalBorrowValue)
    const netWorthApr = totalSupplyValue.minus(totalBorrowValue).eq(0)
      ? new BigNumber(0)
      : totalSupplyValue
          .multipliedBy(totalsupplyApy)
          .minus(totalBorrowValue.multipliedBy(totalBorrowApy))
          .div(totalSupplyValue.minus(totalBorrowValue))
    const hf =
      totalBorrowValue.toNumber() !== 0
        ? maxLiquidationValue.dividedBy(totalBorrowValue).toNumber()
        : Infinity

    return {
      hf,
      netVaule: netVaule.toString(),
      netWorthApr: netWorthApr.toString(),
      totalSupplyValue: totalSupplyValue.toString(),
      totalBorrowValue: totalBorrowValue.toString(),
      totalsupplyApy: totalsupplyApy.toString(),
      totalBorrowApy: totalBorrowApy.toString(),
      maxLiquidationValue: maxLiquidationValue.toString(),
      maxLoanToVaule: maxLoanToVaule.toString(),
      supply,
      borrow
    }
  }
}

export async function verifyHealthFactorPTB(
  tx: Transaction,
  address: string | AccountCap | TransactionResult,
  hf: number,
  options?: Partial<EnvOption & MarketOption>
) {
  const config = await getConfig({
    ...options,
    cacheTime: DEFAULT_CACHE_TIME
  })
  if (config.limter) {
    tx.moveCall({
      target: `${config.limter}::navi_adaptor::verify_navi_position_healthy`,
      arguments: [
        tx.object('0x06'),
        tx.object(config.storage),
        tx.object(config.priceOracle),
        parseTxValue(address, tx.pure.address),
        tx.pure.u256(new BigNumber(hf).shiftedBy(27).toNumber())
      ]
    })
  }
}
